import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { userRequest } from '../api/requestMethods';
import { stashCardAuthContext } from "../utils/cardPayment";
import { withFreightCheckoutConsent } from "./freightCheckout";
import { isScheduleRejectionCode } from "../utils/deliverySchedule";
import { branchRefusalFields, branchRefusalOf } from "../utils/branchAvailability";

const loadOrderFromLocalStorage = () => {
    const savedOrder = localStorage.getItem('healthcareOrder');
    return savedOrder ? JSON.parse(savedOrder) : '';
};

const saveOrderToLocalStorage = (order) => {
    localStorage.setItem('healthcareOrder', JSON.stringify(order));
};

const initialState = {
    order: loadOrderFromLocalStorage(),
    orderCreationStatus: '',
    paymentCreationStatus: 'idle',
    error: null,
};

// MTN Mobile Money
export const payMoMoCheckout = createAsyncThunk(
    'healthcareOrder/payMoMoCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(`/momo/${client_id}`, data);
            return response.data;
        } catch (error) {
            console.log(error.response?.data?.message || error.response?.data?.msg);
            return rejectWithValue(error);
        }
    }
);

// Airtel Money
export const payAirtelMoneyCheckout = createAsyncThunk(
    'healthcareOrder/payAirtelCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(
                `/fw/flutterAirtelPayment/${client_id}`,
                data
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.response?.data?.msg || "Payment could not be started. Please try again.", {
                autoClose: false,
            });
            return rejectWithValue(error.response?.data?.message || error.response?.data?.msg || "Payment failed");
        }
    }
);

// Payment with Credit Card
export const payCardCheckout = createAsyncThunk(
    'healthcareOrder/payCardCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        // Stash sanitized context (last4/brand/amount) for the /pay/authorize page —
        // screens navigate there via window.location, so redux state does not survive.
        stashCardAuthContext(data?.cardDetails, data?.totalPrice);
        try {
            const response = await userRequest.post(
                `/fw/flutterCardPayment/${client_id}`,
                data,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            toast.error((error.response?.data?.message || error.message || "Something went wrong. Please try again."), {
                autoClose: false,
            });
            return rejectWithValue(error.message || 'Unknown Error Occured!');
        }
    }
);

// Card payment PIN verification
export const submitCardPin_Otp = createAsyncThunk(
    'healthcareOrder/submitCardPin_Otp',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(
                `/fw/flutterSubmitCardPin_Otp/${client_id}`,
                data,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            toast.error((error.response?.data?.message || error.message || "Something went wrong. Please try again."), {
                autoClose: false,
            });
            return rejectWithValue(error.message || 'Unknown Error Occured!');
        }
    }
);

export const createOrder = createAsyncThunk(
    'healthcareOrder/createOrder',
    async (data, { rejectWithValue, getState }) => {
        try {
            const checkoutData = withFreightCheckoutConsent(data, getState(), "WEB");
            const res = await userRequest('/mobile', {
                method: 'POST',
                data: checkoutData,
            });
            toast.success('Healthcare order created successfully!');
            return res.data;
        } catch (err) {
            // Serializable payload with the server's code, like the other
            // verticals, so the place-order screen can react to it. Optional
            // chaining: a network error has no response.
            const code = err.response?.data?.code;
            const message =
                err.response?.data?.message ||
                err.message ||
                'Failed to create order. Please try again.';
            // A delivery-time refusal opens the checkout's time picker dialog
            // instead (useCheckoutDeliverySchedule), and a branch refusal opens
            // the cart review (useBranchCartCheck); a toast would double either.
            if (!isScheduleRejectionCode(code) && !branchRefusalOf(err.response?.data)) {
                toast.error(message, {
                    autoClose: false,
                });
            }
            return rejectWithValue({
                status: err.response?.status,
                code,
                message,
                // The flagged lines of a 409 BRANCH_LINES_CHANGED (4 Oct 2026).
                ...branchRefusalFields(err.response?.data),
            });
        }
    }
);

const healthcareOrderSlice = createSlice({
    name: 'healthcareOrder',
    initialState,
    reducers: {
        resetOrderCreation: (state) => {
            state.order = '';
            state.orderCreationStatus = '';
            state.paymentCreationStatus = '';
            state.error = null;
            localStorage.removeItem('healthcareOrder');
        },
        resetHealthcareOrder: (state) => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createOrder.pending, (state) => {
                state.orderCreationStatus = 'pending';
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.orderCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.orderCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payMoMoCheckout.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(payMoMoCheckout.fulfilled, (state) => {
                state.paymentCreationStatus = 'fulfilled';
            })
            .addCase(payMoMoCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payCardCheckout.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(payCardCheckout.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(payCardCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payAirtelMoneyCheckout.pending, (state) => {
                state.paymentCreationStatus = 'processing payment';
            })
            .addCase(payAirtelMoneyCheckout.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(payAirtelMoneyCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(submitCardPin_Otp.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(submitCardPin_Otp.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(submitCardPin_Otp.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            });
    },
});

export const { resetOrderCreation, resetHealthcareOrder } = healthcareOrderSlice.actions;
export default healthcareOrderSlice.reducer;

